import { CATEGORY_PUBLICITY } from '../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { ClipboardList } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import { FormDrawer } from '../../components/Drawer'
import {
  FormBody,
  FormCols,
  FormSection,
  FormSpan,
  SwitchField,
  SwitchGroup,
  Textarea,
} from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { assessmentsStrings as S } from './strings'

const EMPTY = {
  name: '',
  descriptionAr: '',
  nameEn: '',
  descriptionEn: '',
  startDate: '',
  endDate: '',
  enabled: true,
  mandatory: false,
  questionsCount: '10',
  passLimit: '60',
  publicity: String(CATEGORY_PUBLICITY.PUBLIC),
  categoryIds: [],
  groupIds: [],
}

/**
 * قياس جديد / تعديل — the assessment form.
 *
 * The legacy is one modal-xl of fourteen controls in a single column. Here it
 * reads as the three things an assessment actually is: what it is called, when
 * it runs and how it is scored, and which topics it draws its questions from.
 *
 * `التخصيص` filters the topic list rather than posting a value of its own — it
 * is the legacy's `drpDnCategoriesFilter`, which is a view control, not a
 * field. Switching it never silently drops topics already chosen.
 */
export default function AssessmentFormDrawer({
  open,
  assessment,
  categories = [],
  groups = [],
  onClose,
  onSave,
}) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!open) return
    setValues(assessment ? { ...EMPTY, ...assessment } : EMPTY)
    setErrors({})
  }, [open, assessment])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  /* A view filter, not a field: it narrows the topic list and nothing else. */
  const visibleCategories = useMemo(
    () => categories.filter((category) =>
      String(category.publicity ?? CATEGORY_PUBLICITY.PUBLIC) === values.publicity),
    [categories, values.publicity],
  )

  const submit = (event) => {
    event.preventDefault()
    const next = {}
    if (!values.name.trim()) next.name = S.form.required
    if (!values.nameEn.trim()) next.nameEn = S.form.required
    if (!values.startDate) next.startDate = S.form.required
    if (!values.endDate) next.endDate = S.form.required
    if (!String(values.questionsCount).trim()) next.questionsCount = S.form.required
    if (!String(values.passLimit).trim()) next.passLimit = S.form.required
    if (values.categoryIds.length === 0) next.categoryIds = S.form.required
    /* `SaveAssessmentHandler` requires at least one category AND at least one group, so an
       assessment with no audience cannot be saved — it was refused server-side with
       "يجب اختيار فئة ومجموعة واحدة على الأقل" after the form had already been filled in. */
    if (values.groupIds.length === 0) next.groupIds = S.form.required
    setErrors(next)
    if (Object.keys(next).length > 0) return
    onSave?.(values)
  }

  return (
    <FormDrawer
      open={open}
      size="lg"
      formId="assessment-form"
      icon={ClipboardList}
      title={assessment ? S.row.update : S.create}
      subtitle={S.title}
      submitLabel={S.form.save}
      cancelLabel={S.close}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormBody>
        <FormSection>
          <FormCols>
            <FormRow label={S.form.nameAr} error={errors.name}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.name}
                  onChange={set('name')}
                  invalid={!!errors.name}
                  autoComplete="off"
                />
              )}
            </FormRow>

            <FormRow label={S.form.nameEn} error={errors.nameEn}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.nameEn}
                  onChange={set('nameEn')}
                  invalid={!!errors.nameEn}
                  autoComplete="off"
                />
              )}
            </FormRow>

            <FormRow label={S.form.descriptionAr}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={values.descriptionAr}
                  onChange={set('descriptionAr')}
                />
              )}
            </FormRow>

            <FormRow label={S.form.descriptionEn}>
              {(id) => (
                <Textarea
                  id={id}
                  dir="ltr"
                  rows={3}
                  value={values.descriptionEn}
                  onChange={set('descriptionEn')}
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.startDate} error={errors.startDate}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.startDate}
                  onChange={set('startDate')}
                  max={values.endDate || undefined}
                />
              )}
            </FormRow>

            <FormRow label={S.form.endDate} error={errors.endDate}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.endDate}
                  onChange={set('endDate')}
                  min={values.startDate || undefined}
                />
              )}
            </FormRow>

            <FormRow label={S.form.questionsCount} error={errors.questionsCount}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min="1"
                  dir="ltr"
                  value={values.questionsCount}
                  onChange={set('questionsCount')}
                  invalid={!!errors.questionsCount}
                />
              )}
            </FormRow>

            <FormRow label={S.form.passLimit} error={errors.passLimit} hint={S.form.passHint}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min="0"
                  max="100"
                  dir="ltr"
                  value={values.passLimit}
                  onChange={set('passLimit')}
                  invalid={!!errors.passLimit}
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.publicity}>
              {(id) => (
                <Select
                  id={id}
                  value={values.publicity}
                  onChange={set('publicity')}
                  options={[
                    { value: String(CATEGORY_PUBLICITY.PUBLIC), label: S.form.public },
                    { value: String(CATEGORY_PUBLICITY.PRIVATE), label: S.form.private },
                  ]}
                />
              )}
            </FormRow>

            <FormSpan>
              <GroupPicker
                options={visibleCategories}
                selected={values.categoryIds}
                onChange={set('categoryIds')}
                legend={S.form.categories}
                error={errors.categoryIds}
                selectAllLabel={S.form.selectAll}
                filterLabel={S.form.filter}
              />
            </FormSpan>

            {/* THE AUDIENCE. Without this the form could collect categories and nothing
                else, so every create was refused by the server for want of a group. */}
            <FormSpan>
              <GroupPicker
                options={groups}
                selected={values.groupIds}
                onChange={set('groupIds')}
                legend={S.form.groups}
                error={errors.groupIds}
                selectAllLabel={S.form.selectAll}
                filterLabel={S.form.filter}
              />
            </FormSpan>
          </FormCols>
        </FormSection>

        <SwitchGroup>
          <SwitchField checked={values.enabled} onChange={set('enabled')} label={S.form.enabled} />
          <SwitchField
            checked={values.mandatory}
            onChange={set('mandatory')}
            label={S.form.mandatory}
          />
        </SwitchGroup>
      </FormBody>
    </FormDrawer>
  )
}
